import React, { useState, useEffect, useCallback } from 'react'
import {
  ShieldCheck, ShieldAlert, AlertTriangle, Shield, CheckCircle,
  Search, RefreshCw, Terminal, ExternalLink, Copy, Check, Filter,
  Layers, Server
} from 'lucide-react'
import {
  getClients, getShieldVulnerabilities, getShieldCompliance, triggerShieldScan
} from '../api'

export default function CloudIQShield() {
  const [clients, setClients] = useState([])
  const [selectedClient, setSelectedClient] = useState('')
  const [activeTab, setActiveTab] = useState('vulnerabilities') // 'vulnerabilities' | 'compliance'

  // Data states
  const [vulnData, setVulnData] = useState(null)
  const [complianceData, setComplianceData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [scanning, setScanning] = useState(false)

  // Filter states
  const [severityFilter, setSeverityFilter] = useState('')
  const [searchQuery, setSearchQuery] = useState('')
  const [copiedId, setCopiedId] = useState(null)

  const loadClients = useCallback(async () => {
    try {
      const res = await getClients()
      const clientList = Array.isArray(res?.clients) ? res.clients : []
      setClients(clientList)
      if (clientList.length > 0 && !selectedClient) {
        setSelectedClient(clientList[0].id)
      }
    } catch {
      setClients([])
    }
  }, [selectedClient])

  useEffect(() => {
    loadClients()
  }, [loadClients])

  const fetchShieldData = useCallback(async () => {
    if (!selectedClient) {
      setLoading(false)
      return
    }
    setLoading(true)
    try {
      const [vulns, comp] = await Promise.all([
        getShieldVulnerabilities(selectedClient, severityFilter || null, 200),
        getShieldCompliance(selectedClient)
      ])
      setVulnData(vulns)
      setComplianceData(comp)
    } catch {
      setVulnData(null)
      setComplianceData(null)
    } finally {
      setLoading(false)
    }
  }, [selectedClient, severityFilter])

  useEffect(() => {
    fetchShieldData()
  }, [fetchShieldData])

  const handleScanNow = async () => {
    if (!selectedClient) return
    setScanning(true)
    try {
      await triggerShieldScan(selectedClient)
      await fetchShieldData()
    } catch (err) {
      alert(`Security scan failed: ${err.message}`)
    } finally {
      setScanning(false)
    }
  }

  const handleCopyCommand = (command, id) => {
    if (!command) return
    navigator.clipboard.writeText(command)
    setCopiedId(id)
    setTimeout(() => setCopiedId(null), 2000)
  }

  const rawVulns = Array.isArray(vulnData?.vulnerabilities) ? vulnData.vulnerabilities : []
  const filteredVulns = rawVulns.filter((v) => {
    if (!searchQuery.trim()) return true
    const q = searchQuery.toLowerCase()
    return (
      (v.cve && v.cve.toLowerCase().includes(q)) ||
      (v.package_name && v.package_name.toLowerCase().includes(q)) ||
      (v.hostname && v.hostname.toLowerCase().includes(q)) ||
      (v.title && v.title.toLowerCase().includes(q))
    )
  })

  const summary = vulnData?.summary || { critical: 0, high: 0, medium: 0, low: 0, total: 0 }
  const avgCis = complianceData?.average_score
  const policies = Array.isArray(complianceData?.policies) ? complianceData.policies : []

  return (
    <div className="animate-in" style={{ maxWidth: 1280, margin: '0 auto' }}>
      {/* Top Header */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: 24,
        flexWrap: 'wrap',
        gap: 16
      }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{
              background: '#EEF2FF',
              padding: 7,
              borderRadius: 8,
              border: '1.5px solid var(--border-ink)',
              boxShadow: 'var(--shadow-solid-sm)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'var(--brand-blue)'
            }}>
              <ShieldCheck size={20} />
            </div>
            <h2 style={{ fontFamily: 'var(--font-serif)', fontSize: 22, fontWeight: 800, margin: 0, color: 'var(--ink-900)' }}>
              CloudIQ Shield &mdash; Security Posture &amp; CVE Scanner
            </h2>
          </div>
          <p style={{ margin: '4px 0 0', color: 'var(--ink-600)', fontSize: 13 }}>
            Continuous vulnerability correlation against the National Vulnerability Database (NVD) and CIS Linux Benchmark compliance scoring.
          </p>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <select
            value={selectedClient}
            onChange={(e) => setSelectedClient(e.target.value)}
            className="input-select"
            style={{
              padding: '7px 12px',
              borderRadius: 8,
              border: '1.5px solid var(--border-ink)',
              fontSize: 13,
              fontWeight: 600,
              background: '#FFFFFF',
              boxShadow: 'var(--shadow-solid-sm)',
              color: 'var(--ink-900)'
            }}
          >
            {clients.map((c) => (
              <option key={c.id} value={c.id}>{c.client_name || c.id}</option>
            ))}
          </select>

          <button
            type="button"
            onClick={handleScanNow}
            disabled={scanning || !selectedClient}
            className="btn btn-primary btn-sm"
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              border: '2px solid var(--border-ink)',
              boxShadow: 'var(--shadow-solid-sm)',
              borderRadius: 8,
              fontWeight: 700
            }}
          >
            <RefreshCw size={14} className={scanning ? 'spin' : ''} />
            {scanning ? 'Scanning Nodes…' : 'Run Security Scan'}
          </button>
        </div>
      </div>

      {/* KPI Cards */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
        gap: 16,
        marginBottom: 24
      }}>
        {/* Critical */}
        <div style={{
          background: '#FFFFFF',
          padding: '16px 20px',
          borderRadius: 12,
          border: '2px solid var(--border-ink)',
          borderLeft: '6px solid var(--brand-wine)',
          boxShadow: 'var(--shadow-solid-sm)'
        }}>
          <div style={{ fontSize: 11, fontWeight: 800, color: 'var(--brand-wine)', display: 'flex', alignItems: 'center', gap: 6, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            <ShieldAlert size={14} /> CRITICAL (CVSS &ge; 9.0)
          </div>
          <div style={{ fontFamily: 'var(--font-mono)', fontSize: 26, fontWeight: 800, color: 'var(--brand-wine)', marginTop: 4 }}>
            {summary.critical}
          </div>
          <div style={{ fontSize: 12, color: 'var(--ink-500)', marginTop: 2 }}>Requires immediate upgrade</div>
        </div>

        {/* High */}
        <div style={{
          background: '#FFFFFF',
          padding: '16px 20px',
          borderRadius: 12,
          border: '2px solid var(--border-ink)',
          borderLeft: '6px solid var(--brand-coral)',
          boxShadow: 'var(--shadow-solid-sm)'
        }}>
          <div style={{ fontSize: 11, fontWeight: 800, color: 'var(--brand-coral)', display: 'flex', alignItems: 'center', gap: 6, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            <AlertTriangle size={14} /> HIGH (CVSS 7.0–8.9)
          </div>
          <div style={{ fontFamily: 'var(--font-mono)', fontSize: 26, fontWeight: 800, color: 'var(--brand-coral)', marginTop: 4 }}>
            {summary.high}
          </div>
          <div style={{ fontSize: 12, color: 'var(--ink-500)', marginTop: 2 }}>Next maintenance window</div>
        </div>

        {/* Medium & Low */}
        <div style={{
          background: '#FFFFFF',
          padding: '16px 20px',
          borderRadius: 12,
          border: '2px solid var(--border-ink)',
          borderLeft: '6px solid #D97706',
          boxShadow: 'var(--shadow-solid-sm)'
        }}>
          <div style={{ fontSize: 11, fontWeight: 800, color: '#B45309', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            MEDIUM &amp; LOW CVEs
          </div>
          <div style={{ fontFamily: 'var(--font-mono)', fontSize: 26, fontWeight: 800, color: '#B45309', marginTop: 4 }}>
            {summary.medium + summary.low}
          </div>
          <div style={{ fontSize: 12, color: 'var(--ink-500)', marginTop: 2 }}>Standard package patching</div>
        </div>

        {/* Total CVEs */}
        <div style={{
          background: '#FFFFFF',
          padding: '16px 20px',
          borderRadius: 12,
          border: '2px solid var(--border-ink)',
          borderLeft: '6px solid var(--border-ink)',
          boxShadow: 'var(--shadow-solid-sm)'
        }}>
          <div style={{ fontSize: 11, fontWeight: 800, color: 'var(--ink-600)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            Total CVE Findings
          </div>
          <div style={{ fontFamily: 'var(--font-mono)', fontSize: 26, fontWeight: 800, color: 'var(--ink-900)', marginTop: 4 }}>
            {summary.total}
          </div>
          <div style={{ fontSize: 12, color: 'var(--ink-500)', marginTop: 2 }}>Across {vulnData?.agent_count || 0} enrolled nodes</div>
        </div>

        {/* CIS Benchmark Score */}
        <div style={{
          background: '#FFFFFF',
          padding: '16px 20px',
          borderRadius: 12,
          border: '2px solid var(--border-ink)',
          borderLeft: '6px solid #059669',
          boxShadow: 'var(--shadow-solid-sm)'
        }}>
          <div style={{ fontSize: 11, fontWeight: 800, color: '#047857', display: 'flex', alignItems: 'center', gap: 6, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            <CheckCircle size={14} /> CIS Benchmark Compliance
          </div>
          <div style={{ fontFamily: 'var(--font-mono)', fontSize: 26, fontWeight: 800, color: '#047857', marginTop: 4 }}>
            {avgCis !== null && avgCis !== undefined ? `${avgCis}%` : 'N/A'}
          </div>
          <div style={{ fontSize: 12, color: 'var(--ink-500)', marginTop: 2 }}>Hardening assessment</div>
        </div>
      </div>

      {/* Tabs */}
      <div style={{
        display: 'flex',
        gap: 8,
        borderBottom: '1.5px solid var(--border-ink)',
        paddingBottom: 10,
        marginBottom: 20
      }}>
        <button
          type="button"
          onClick={() => setActiveTab('vulnerabilities')}
          className={`btn ${activeTab === 'vulnerabilities' ? 'btn-primary' : 'btn-secondary'}`}
          style={{
            padding: '8px 18px',
            fontSize: 13,
            border: '2px solid var(--border-ink)',
            boxShadow: 'var(--shadow-solid-sm)',
            fontWeight: 700
          }}
        >
          <ShieldAlert size={14} style={{ marginRight: 6 }} />
          CVE Vulnerabilities ({summary.total})
        </button>

        <button
          type="button"
          onClick={() => setActiveTab('compliance')}
          className={`btn ${activeTab === 'compliance' ? 'btn-primary' : 'btn-secondary'}`}
          style={{
            padding: '8px 18px',
            fontSize: 13,
            border: '2px solid var(--border-ink)',
            boxShadow: 'var(--shadow-solid-sm)',
            fontWeight: 700
          }}
        >
          <CheckCircle size={14} style={{ marginRight: 6 }} />
          CIS Benchmark Compliance ({policies.length} Policies)
        </button>
      </div>

      {/* Tab 1: CVE Vulnerability Explorer */}
      {activeTab === 'vulnerabilities' && (
        <div style={{
          background: '#FFFFFF',
          borderRadius: 12,
          border: '2px solid var(--border-ink)',
          boxShadow: 'var(--shadow-solid-md)',
          overflow: 'hidden'
        }}>
          {/* Filter Bar */}
          <div style={{
            padding: '16px 20px',
            borderBottom: '2px solid var(--border-ink)',
            background: 'var(--canvas-subtle)',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: 12
          }}>
            {/* Severity Pills */}
            <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
              <span style={{ fontSize: 12, fontWeight: 800, color: 'var(--ink-700)', marginRight: 4, textTransform: 'uppercase' }}>Severity:</span>
              {[
                { label: 'All', value: '' },
                { label: 'Critical', value: 'CRITICAL' },
                { label: 'High', value: 'HIGH' },
                { label: 'Medium', value: 'MEDIUM' },
                { label: 'Low', value: 'LOW' }
              ].map((pill) => {
                const active = severityFilter === pill.value
                return (
                  <button
                    key={pill.value}
                    type="button"
                    onClick={() => setSeverityFilter(pill.value)}
                    style={{
                      padding: '4px 12px',
                      borderRadius: 14,
                      border: '1.5px solid var(--border-ink)',
                      background: active ? 'var(--ink-900)' : '#FFFFFF',
                      color: active ? '#FFFFFF' : 'var(--ink-800)',
                      fontSize: 12,
                      fontWeight: 700,
                      cursor: 'pointer',
                      boxShadow: active ? 'var(--shadow-solid-sm)' : 'none'
                    }}
                  >
                    {pill.label}
                  </button>
                )
              })}
            </div>

            {/* Search Input */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              background: '#FFFFFF',
              padding: '6px 12px',
              borderRadius: 8,
              border: '1.5px solid var(--border-ink)',
              boxShadow: 'var(--shadow-solid-sm)',
              minWidth: 260
            }}>
              <Search size={14} color="var(--ink-400)" />
              <input
                type="text"
                placeholder="Search CVE, package, or node…"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                style={{ border: 'none', background: 'transparent', outline: 'none', fontSize: 13, width: '100%', color: 'var(--ink-900)' }}
              />
            </div>
          </div>

          {/* CVE Table */}
          {loading ? (
            <div style={{ padding: 40, textAlign: 'center', color: 'var(--ink-500)', fontSize: 13 }}>Loading security findings…</div>
          ) : filteredVulns.length === 0 ? (
            <div style={{ padding: 48, textAlign: 'center', color: 'var(--ink-600)' }}>
              <div style={{
                width: 48,
                height: 48,
                borderRadius: '50%',
                background: '#ECFDF5',
                border: '1.5px solid var(--border-ink)',
                color: '#059669',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 14px'
              }}>
                <ShieldCheck size={24} />
              </div>
              <h3 style={{ fontFamily: 'var(--font-serif)', fontSize: 18, fontWeight: 800, margin: '0 0 6px', color: 'var(--ink-900)' }}>
                {summary.total === 0 ? 'No CVE vulnerabilities detected on enrolled fleet nodes.' : 'No vulnerabilities matching your active filters.'}
              </h3>
              <p style={{ margin: 0, fontSize: 13, color: 'var(--ink-600)' }}>
                {summary.total === 0 ? 'All packages are aligned with current vendor security releases.' : 'Try clearing your search query or selecting a different severity.'}
              </p>
            </div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr style={{ background: 'var(--canvas-subtle)', textAlign: 'left', borderBottom: '2px solid var(--border-ink)' }}>
                    <th style={{ padding: '10px 16px', fontSize: 11, fontWeight: 800, color: 'var(--ink-700)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Vulnerability (CVE)</th>
                    <th style={{ padding: '10px 16px', fontSize: 11, fontWeight: 800, color: 'var(--ink-700)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Affected Package &amp; Host</th>
                    <th style={{ padding: '10px 16px', fontSize: 11, fontWeight: 800, color: 'var(--ink-700)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Severity &amp; Score</th>
                    <th style={{ padding: '10px 16px', fontSize: 11, fontWeight: 800, color: 'var(--ink-700)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Deterministic Remediation</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredVulns.map((v) => {
                    const isCrit = v.severity === 'CRITICAL'
                    const isHigh = v.severity === 'HIGH'
                    const isMed = v.severity === 'MEDIUM'

                    const badgeColor = isCrit ? 'var(--brand-wine)' : isHigh ? 'var(--brand-coral)' : isMed ? '#B45309' : '#047857'
                    const badgeBg = isCrit ? 'var(--brand-wine-bg)' : isHigh ? 'var(--brand-coral-bg)' : isMed ? '#FEF3C7' : '#DEF7EC'
                    const badgeBorder = isCrit ? 'var(--brand-wine-border)' : isHigh ? 'var(--brand-coral-border)' : isMed ? '#D97706' : '#10B981'

                    return (
                      <tr key={v.id} style={{ borderBottom: '1px solid var(--border-subtle)' }}>
                        {/* CVE Column */}
                        <td style={{ padding: '14px 16px', verticalAlign: 'top' }}>
                          <a
                            href={`https://nvd.nist.gov/vuln/detail/${v.cve}`}
                            target="_blank"
                            rel="noreferrer"
                            style={{ fontWeight: 800, color: 'var(--brand-blue)', textDecoration: 'none', display: 'flex', alignItems: 'center', gap: 4, fontFamily: 'var(--font-mono)' }}
                          >
                            {v.cve} <ExternalLink size={12} />
                          </a>
                          <div style={{ fontSize: 12, color: 'var(--ink-600)', marginTop: 4, maxWidth: 320, lineHeight: 1.4 }}>
                            {v.title}
                          </div>
                        </td>

                        {/* Host & Package Column */}
                        <td style={{ padding: '14px 16px', verticalAlign: 'top' }}>
                          <div style={{ fontWeight: 700, color: 'var(--ink-900)' }}>{v.package_name}</div>
                          <div style={{ fontSize: 12, color: 'var(--ink-500)', marginTop: 2, fontFamily: 'var(--font-mono)' }}>
                            v{v.installed_version} ({v.architecture || 'all'})
                          </div>
                          <div style={{ fontSize: 11, color: 'var(--ink-700)', marginTop: 4, display: 'flex', alignItems: 'center', gap: 4 }}>
                            <Server size={11} /> {v.hostname}
                          </div>
                        </td>

                        {/* Severity Column */}
                        <td style={{ padding: '14px 16px', verticalAlign: 'top' }}>
                          <span style={{
                            padding: '3px 8px',
                            borderRadius: 6,
                            fontSize: 11,
                            fontWeight: 800,
                            color: badgeColor,
                            background: badgeBg,
                            border: `1.5px solid ${badgeBorder}`,
                            display: 'inline-block'
                          }}>
                            ● {v.severity}
                          </span>
                          {v.cvss_score !== null && (
                            <div style={{ fontSize: 11, color: 'var(--ink-500)', marginTop: 4, fontFamily: 'var(--font-mono)' }}>
                              CVSS v3: <strong>{v.cvss_score}</strong>
                            </div>
                          )}
                        </td>

                        {/* Remediation Column */}
                        <td style={{ padding: '14px 16px', verticalAlign: 'top' }}>
                          <div style={{
                            background: '#121826',
                            padding: '8px 12px',
                            borderRadius: 6,
                            color: '#4ADE80',
                            fontSize: 12,
                            fontFamily: 'var(--font-mono)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            gap: 10,
                            border: '1.5px solid var(--border-ink)'
                          }}>
                            <code style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {v.remediation_command}
                            </code>
                            <button
                              type="button"
                              onClick={() => handleCopyCommand(v.remediation_command, v.id)}
                              style={{
                                background: '#1E293B',
                                border: '1px solid #334155',
                                borderRadius: 4,
                                padding: '3px 8px',
                                color: '#F1F5F9',
                                cursor: 'pointer',
                                fontSize: 11,
                                display: 'flex',
                                alignItems: 'center',
                                gap: 4
                              }}
                            >
                              {copiedId === v.id ? <Check size={12} color="#4ADE80" /> : <Copy size={12} />}
                              {copiedId === v.id ? 'Copied' : 'Copy'}
                            </button>
                          </div>
                          <div style={{ fontSize: 11, color: 'var(--ink-500)', marginTop: 4 }}>
                            Non-destructive package update (preserves custom configuration).
                          </div>
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Tab 2: CIS Benchmark Compliance (SCA) */}
      {activeTab === 'compliance' && (
        <div style={{
          background: '#FFFFFF',
          borderRadius: 12,
          border: '2px solid var(--border-ink)',
          boxShadow: 'var(--shadow-solid-md)',
          padding: 24
        }}>
          <h3 style={{ margin: '0 0 16px', fontFamily: 'var(--font-serif)', fontSize: 18, fontWeight: 800, color: 'var(--ink-900)' }}>
            Center for Internet Security (CIS) Linux Benchmark Assessments
          </h3>

          {policies.length === 0 ? (
            <div style={{ padding: 40, textAlign: 'center', color: 'var(--ink-500)', fontSize: 13 }}>
              No CIS benchmark policies currently evaluated for this client.
            </div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: 16 }}>
              {policies.map((pol, idx) => {
                const score = pol.score_percent || 0
                const isPassing = score >= 80.0

                return (
                  <div key={idx} style={{
                    background: '#FFFFFF',
                    borderRadius: 10,
                    border: '1.5px solid var(--border-ink)',
                    boxShadow: 'var(--shadow-solid-sm)',
                    padding: 16
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span style={{ fontSize: 12, fontWeight: 800, color: 'var(--ink-900)' }}>{pol.hostname}</span>
                      <span style={{
                        padding: '2px 8px',
                        borderRadius: 6,
                        fontSize: 11,
                        fontWeight: 800,
                        color: isPassing ? '#047857' : 'var(--brand-coral)',
                        background: isPassing ? '#DEF7EC' : 'var(--brand-coral-bg)',
                        border: isPassing ? '1px solid #10B981' : '1px solid var(--brand-coral-border)'
                      }}>
                        {pol.status}
                      </span>
                    </div>

                    <h4 style={{ margin: '10px 0 6px', fontSize: 14, fontWeight: 700, color: 'var(--ink-900)' }}>{pol.name}</h4>

                    {/* Progress Bar */}
                    <div style={{ background: 'var(--canvas-subtle)', borderRadius: 4, height: 8, overflow: 'hidden', margin: '8px 0', border: '1px solid var(--border-subtle)' }}>
                      <div style={{ background: isPassing ? '#10B981' : 'var(--brand-coral)', width: `${score}%`, height: '100%' }} />
                    </div>

                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: 'var(--ink-600)' }}>
                      <span>Score: <strong>{score}%</strong></span>
                      <span>Passed: <strong>{pol.pass_count}</strong> / Failed: <strong>{pol.fail_count}</strong></span>
                    </div>
                  </div>
                )
              })}
            </div>
          )}
        </div>
      )}
    </div>
  )
}
